import React, { useEffect, useState } from 'react'
import { type CurrencyExchangeInstructionDetails } from '../../../../api-schema-types/financial/CurrencyExchangeDomainDtos'
import { useCreateBatchMutation } from '../../../../api-services/financial/currencyExchangeApi'
import { RtkQueryHelper } from '../../../../api-services/shared/RtkQueryHelper'
import { useAppDispatch } from '../../../../app/hooks'
import { Button, FormControl, Grid, InputLabel, MenuItem, Select, type SelectChangeEvent } from '@mui/material'
import { setNotification } from '../../../components/ui-widgets/uiWidgetsSlice'

interface CreateBatchProps {
  pendingInstructions: CurrencyExchangeInstructionDetails[]
}

const CreateBatch: React.FC<CreateBatchProps> = ({ pendingInstructions }: CreateBatchProps) => {
  const dispatch = useAppDispatch()
  const [fromCurrencyCode, setFromCurrencyCode] = useState<string>('')
  const [toCurrencyCode, setToCurrencyCode] = useState<string>('')

  const pendingFromCurrencyCodes: string[] = []
  const set = new Set(pendingInstructions.map((i) => i.fromCurrencyCode))
  set.forEach((c) => pendingFromCurrencyCodes.push(c))

  const pendingToCurrencyCodes: string[] = []
  const toSet = new Set(pendingInstructions.map((i) => i.toCurrencyCode))
  toSet.forEach((c) => pendingToCurrencyCodes.push(c))

  // =================================================================================================================

  const [apiCreateBatchTrigger, apiCreateBatchResult] = useCreateBatchMutation()

  const handleFromCurrencyChange = (evt: SelectChangeEvent<string>): void => { setFromCurrencyCode(evt.target.value) }
  const handleToCurrencyChange = (evt: SelectChangeEvent<string>): void => { setToCurrencyCode(evt.target.value) }

  const handleFormSubmit = async (): Promise<void> => {
    await apiCreateBatchTrigger({ fromCurrencyCode, toCurrencyCode })
  }

  useEffect(() => {
    if (apiCreateBatchResult.isSuccess) {
      dispatch(setNotification({ open: true, severity: 'success', message: `Batch ${apiCreateBatchResult.data.batchId} created.` }))
      setFromCurrencyCode('')
      setToCurrencyCode('')
    }

    if (apiCreateBatchResult.isError) {
      dispatch(setNotification({
        open: true,
        severity: 'error',
        message: RtkQueryHelper.mapResponseError(apiCreateBatchResult.error)
      }))
    }
  }, [apiCreateBatchResult.isSuccess, apiCreateBatchResult.isError])

  // =================================================================================================================

  return (
    <>
      <Grid container spacing={2} >
        <Grid item md={2}>
          <FormControl sx={{ width: '100%' }}>
            <InputLabel>From Currency</InputLabel>
            <Select label={'From Currency'}
                    disabled={pendingInstructions.length === 0}
                    onChange={handleFromCurrencyChange}
                    value={fromCurrencyCode}>
              {
                pendingFromCurrencyCodes.map((c) =>
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                )
              }
            </Select>
          </FormControl>
        </Grid>

        <Grid item md={2}>
          <FormControl sx={{ width: '100%' }}>
            <InputLabel>To Currency</InputLabel>
            <Select label={'To Currency'}
                    disabled={pendingInstructions.length === 0}
                    onChange={handleToCurrencyChange}
                    value={toCurrencyCode}>
              {
                pendingToCurrencyCodes.map((c) =>
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                )
              }
            </Select>
          </FormControl>
        </Grid>

        <Grid item md={4} justifyContent={'flex-end'}>
          <Button type={'button'} variant={'contained'}
                  disabled={pendingInstructions.length === 0}
                  onClick={handleFormSubmit}>
            Create Batch
          </Button>

        </Grid>
      </Grid>
    </>
  )
}

export default CreateBatch
